import type { ResponseMigrateApproval } from "@/features/migrate/types/api";
import type { SupWeb3Address } from "@/types/web3";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";

import { getAddress } from "viem";
import { SupX, SupXCircle, SupButton, SupLoader, SupInfoCircle, SupText } from "@repo/core-ui";
import { checkIfRejected, depositTransferError, notifyMessage } from "@/utils";
import { useEstimateGas, useSendTransaction, useWaitForTransactionReceipt } from "wagmi";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";

interface MigrateOrganismTxApprovalActionProps {
  approvalData: ResponseMigrateApproval;
  chainId: number;
  onSuccess?: (approvalData: ResponseMigrateApproval) => void;
}

export default function MigrateOrganismTxApprovalAction({ approvalData, onSuccess, chainId }: MigrateOrganismTxApprovalActionProps) {
  const operation = {
    name: "Approve",
    action: "Pending...",
    icon: <SupInfoCircle />,
  };

  const userAddress = useWeb3UserAddress();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined | null>(undefined);
  const [buttonText, setButtonText] = useState<string>(operation.name);

  const rawArgumentsForPrepareSendTx = useMemo(
    () => ({
      data: approvalData.transaction_data as SupWeb3Address,
      to: approvalData.to ? getAddress(approvalData.to) : ("" as SupWeb3Address),
      value: 0n,
      account: userAddress,
    }),
    [approvalData.to, approvalData.transaction_data, userAddress]
  );

  const { data: gasData, error: prepareError, isLoading: isPrepareLoading } = useEstimateGas(rawArgumentsForPrepareSendTx);

  const { data, sendTransaction, error: sendError, isPending: isSendLoading } = useSendTransaction();

  const {
    isLoading: isWaitLoading,
    isSuccess: isWaitSuccess,
    error: waitError,
  } = useWaitForTransactionReceipt({
    hash: data,
    confirmations: 2,
  });

  // Setting the loading state
  useEffect(() => {
    if (isPrepareLoading) {
      setButtonText("Preparing tx...");
    }
    if (isSendLoading) {
      setButtonText("Signing tx... (check the wallet)");
    }
    if (isWaitLoading) {
      setButtonText("Processing tx...");
    }
    if (error) {
      setButtonText(checkIfRejected(error) ? operation.name : `Can't do ${operation.name}`);
    } else {
      if (!isLoading) {
        setButtonText(operation.name);
      }
    }
  }, [isPrepareLoading, isSendLoading, isWaitLoading, error, isLoading, operation.name]);

  useEffect(() => {
    setIsLoading(isPrepareLoading || isSendLoading || isWaitLoading);
  }, [isWaitLoading, isSendLoading, isPrepareLoading]);

  useEffect(() => {
    if (data) {
      onSuccess?.(approvalData);
      notifyMessage(operation.action, `Processing tx #${data}`, "info", operation.icon, approvalData.transaction_data);
    }
  }, [approvalData, approvalData.transaction_data, data, onSuccess, operation.action, operation.icon]);

  useEffect(() => {
    if (isWaitSuccess) {
      notifyMessage(operation.action, `TX created successfully!`, "success", operation.icon, approvalData.transaction_data);
      setIsLoading(false);
    }
  }, [isWaitSuccess, approvalData.transaction_data, operation.action, operation.icon]);

  useEffect(() => {
    if (error) {
      if (checkIfRejected(error)) {
        notifyMessage(operation.action, `Transaction rejected`, "warning", operation.icon, approvalData.transaction_data);
        setError(undefined);
      }
      setIsLoading(false);
    }
  }, [error, approvalData.transaction_data, operation.action, operation.icon]);

  useEffect(() => {
    setError(prepareError || sendError || waitError);
  }, [waitError, sendError, prepareError]);

  const callTransfer = useCallback(() => {
    setIsLoading(true);
    try {
      approvalData &&
        sendTransaction?.({
          ...rawArgumentsForPrepareSendTx,
          gas: gasData ? (gasData * 110n) / 100n : undefined,
        });
    } catch (e) {
      console.log("DEBUG: tx error", {
        e,
        rawArgumentsForPrepareSendTx,
        gas: gasData ? (gasData * 110n) / 100n : undefined,
        approvalData,
      });
      notifyMessage(`${operation.name} error`, depositTransferError(e as Error), "error", <SupXCircle />, approvalData.transaction_data, 0);
      setIsLoading(false);
    }
  }, [approvalData, sendTransaction, rawArgumentsForPrepareSendTx, gasData, operation.name]);

  const prefixIcon = useMemo(() => {
    return isLoading ? <SupLoader isSmall color="primary" className="text-neutral-700" svgClassName="stroke-2" /> : !sendTransaction ? <SupX /> : undefined;
  }, [isLoading, sendTransaction]);

  return (
    <Fragment>
      {error && (
        <div className="bg-error-50 px-4 py-2">
          <SupText variant="subtitle2" className="text-error-925">
            {depositTransferError(error, chainId).trim()}
          </SupText>
        </div>
      )}
      <SupButton
        prefixIcon={prefixIcon}
        disabled={!sendTransaction || isLoading || !!error}
        className={twMerge("w-full justify-center", (!sendTransaction || isLoading) && "cursor-not-allowed")}
        color={!sendTransaction && !isLoading ? "error" : "primary"}
        onClickCapture={callTransfer}
      >
        {buttonText}
      </SupButton>
    </Fragment>
  );
}
